import React, {useEffect, useRef, useState} from 'react';
import AnswerBox from '../../components/AnswerBox';
import {
  LessonContentMChoice,
  LessonContentMChoiceAnswer,
} from '../../../../data/models/LessonContent/LessonContentBase';
import AnswerCircle from '../../components/AnswerCircle';
import Button from '../../../../uiToolkit/Buttons/Button';
import './styles.css';
import ChallengeInfoHeader from '../ChallengeInfoHeader/ChallengeInfoHeader';
import {tapyblPlayerText} from '../../tapyblPlayerText';

interface Props {
  mChoice: LessonContentMChoice;
  onSelectChoice: (
    choices: LessonContentMChoiceAnswer[],
    waitForFeedback?: boolean,
  ) => void;
  onContinueChoice: () => void;
  passingScore?: number;
}

const answerKey = (answer: LessonContentMChoiceAnswer) =>
  answer.optionId || answer.value;

const MCHoiceOverlay = React.memo(
  ({mChoice, onSelectChoice, onContinueChoice, passingScore}: Props) => {
    const [selectedAnswers, setSelectedAnswers] = useState<
      LessonContentMChoiceAnswer[]
    >([]);
    const [submitted, setSubmitted] = useState(false);
    const submittedAnswer = useRef(false);
    const advanced = useRef(false);
    const feedback = mChoice.feedback?.trim();

    useEffect(() => {
      setSelectedAnswers([]);
      setSubmitted(false);
      submittedAnswer.current = false;
      advanced.current = false;
    }, [mChoice]);

    const onClick = () => {
      if (selectedAnswers.length === 0 || advanced.current) return;
      if (feedback) {
        if (!submittedAnswer.current) {
          submittedAnswer.current = true;
          // Record the committed attempt before revealing authored feedback.
          onSelectChoice(selectedAnswers, true);
          setSubmitted(true);
        } else {
          advanced.current = true;
          onContinueChoice();
        }
      } else {
        advanced.current = true;
        onSelectChoice(selectedAnswers);
      }
    };

    const onSelectAnswer = (answer: LessonContentMChoiceAnswer) => {
      if (submittedAnswer.current) return;
      if (selectedAnswers.find(item => answerKey(answer) === answerKey(item))) {
        const newAnswers = [...selectedAnswers];
        newAnswers.splice(
          selectedAnswers.findIndex(
            item => answerKey(answer) === answerKey(item),
          ),
          1,
        );
        setSelectedAnswers(newAnswers);
      } else {
        const newAnswers = [...selectedAnswers];
        newAnswers.push(answer);
        setSelectedAnswers(newAnswers);
      }
    };

    const onSelectSingleAnswer = (answer: LessonContentMChoiceAnswer) => {
      if (submittedAnswer.current) return;
      setSelectedAnswers([answer]);
    };

    const haveOnlyOneAnswer = () => {
      let amountOfRightAnswers = 0;
      mChoice.answers.forEach(answer => {
        if (answer.isCorrect) {
          amountOfRightAnswers++;
        }
      });
      return amountOfRightAnswers === 1;
    };

    return (
      <div className="MChoiceOverlay">
        <ChallengeInfoHeader
          passingScore={passingScore}
          typeIcon="icon-typeMultiple-choice"
          typeTitle={tapyblPlayerText.multipleChoice}
          haveMultipleAnswers={!haveOnlyOneAnswer()}
          isGradable
          className="MChoiceInfoHeader"
        />
        <div className="Content">
          <p className="Question">{mChoice.question}</p>
          <div
            className="Answers"
            role={haveOnlyOneAnswer() ? 'radiogroup' : 'group'}
            aria-label="Answer options"
            aria-disabled={submitted}>
            {mChoice.answers.map((item, index) =>
              haveOnlyOneAnswer() ? (
                <AnswerCircle
                  disabled={submitted}
                  answer={item.value}
                  selected={
                    selectedAnswers.find(
                      answer => answerKey(answer) === answerKey(item),
                    ) !== undefined
                  }
                  key={index.toString()}
                  onClick={() => onSelectSingleAnswer(item)}
                />
              ) : (
                <AnswerBox
                  disabled={submitted}
                  answer={item.value}
                  selected={
                    selectedAnswers.find(
                      answer => answerKey(answer) === answerKey(item),
                    ) !== undefined
                  }
                  key={index.toString()}
                  onClick={() => onSelectAnswer(item)}
                />
              ),
            )}
          </div>
          {submitted && feedback && (
            <div className="AnswerFeedback" role="status" aria-live="polite">
              <h3>{tapyblPlayerText.answerExplanation}</h3>
              <p>{feedback}</p>
            </div>
          )}
          <Button
            title={
              feedback && !submitted
                ? tapyblPlayerText.submitAnswer
                : tapyblPlayerText.continue
            }
            onClick={onClick}
            className="AnswersSubmit"
            disabled={selectedAnswers.length === 0}
            uiType="secondary"
          />
        </div>
      </div>
    );
  },
);

export default MCHoiceOverlay;
